import { useState, useEffect } from 'react'
import { useGetEmailTemplates, useUpdateEmailTemplate, useResetEmailTemplate } from '../../../api/routes/email-templates'
import { toast } from 'react-toastify'
import { RotateCcw, Save, Eye, Code, Mail } from 'lucide-react'

type Template = {
    slug: string
    subject: string
    body: string
    isCustom: boolean
}

const TEMPLATE_LABELS: Record<string, { label: string; description: string; variables: string[] }> = {
    invite: {
        label: 'Invitation Email',
        description: 'Sent when a new user is invited to the platform.',
        variables: ['firstName', 'url', 'email'],
    },
    reset_password: {
        label: 'Password Reset Email',
        description: 'Sent when a user requests a password reset.',
        variables: ['firstName', 'url', 'email'],
    },
}

const SAMPLE_VARIABLES: Record<string, string> = {
    firstName: 'John',
    url: '#',
    email: 'john@example.com',
}

export const SettingsRoute = () => {
    const { data: templates, isLoading } = useGetEmailTemplates()
    const updateTemplate = useUpdateEmailTemplate()
    const resetTemplate = useResetEmailTemplate()

    const [activeSlug, setActiveSlug] = useState<string | null>(null)
    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')
    const [showPreview, setShowPreview] = useState(false)
    const [dirty, setDirty] = useState(false)

    const activeTemplate = templates?.find((t: Template) => t.slug === activeSlug)

    useEffect(() => {
        if (templates?.length && !activeSlug) {
            setActiveSlug(templates[0].slug)
        }
    }, [templates, activeSlug])

    useEffect(() => {
        if (activeTemplate) {
            setSubject(activeTemplate.subject)
            setBody(activeTemplate.body)
            setDirty(false)
            setShowPreview(false)
        }
    }, [activeSlug, activeTemplate?.isCustom])

    const handleSave = async () => {
        if (!activeSlug) return
        try {
            await updateTemplate.mutateAsync({ slug: activeSlug, subject, body })
            setDirty(false)
            toast.success('Template saved')
        } catch {
            toast.error('Failed to save template')
        }
    }

    const handleReset = async () => {
        if (!activeSlug) return
        try {
            await resetTemplate.mutateAsync(activeSlug)
            setDirty(false)
            toast.success('Template reset to default')
        } catch {
            toast.error('Failed to reset template')
        }
    }

    const renderPreview = (html: string) => {
        return html.replace(/\{\{(\w+)\}\}/g, (_, key) => {
            return `<span style="background:#e0e7ff;color:#3730a3;padding:1px 4px;border-radius:3px;font-size:13px;">${SAMPLE_VARIABLES[key] ?? key}</span>`
        })
    }

    if (isLoading) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="animate-pulse space-y-4">
                    <div className="h-7 w-40 bg-muted rounded" />
                    <div className="h-64 bg-muted rounded-lg" />
                </div>
            </div>
        )
    }

    const meta = activeSlug ? TEMPLATE_LABELS[activeSlug] : null

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">Settings</h1>
                <p className="text-sm text-muted-foreground mt-1">Manage platform configuration</p>
            </div>

            <div className="mb-6">
                <h2 className="text-base font-medium text-foreground flex items-center gap-2 mb-4">
                    <Mail size={18} />
                    Email Templates
                </h2>

                <div className="flex gap-2 mb-4">
                    {templates?.map((t: Template) => (
                        <button
                            key={t.slug}
                            onClick={() => {
                                if (dirty && !confirm('You have unsaved changes. Switch anyway?')) return
                                setActiveSlug(t.slug)
                            }}
                            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                                t.slug === activeSlug
                                    ? 'bg-foreground text-background border-foreground'
                                    : 'bg-card text-foreground border-border hover:bg-accent'
                            }`}
                        >
                            {TEMPLATE_LABELS[t.slug]?.label ?? t.slug}
                        </button>
                    ))}
                </div>

                {activeSlug && meta && (
                    <div className="border border-border rounded-lg bg-card">
                        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">{meta.description}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Variables:{' '}
                                    {meta.variables.map((v) => (
                                        <code key={v} className="mx-0.5 px-1 py-0.5 bg-muted rounded text-xs">
                                            {`{{${v}}}`}
                                        </code>
                                    ))}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowPreview(!showPreview)}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-border bg-card hover:bg-accent transition-colors"
                                >
                                    {showPreview ? <Code size={14} /> : <Eye size={14} />}
                                    {showPreview ? 'Edit' : 'Preview'}
                                </button>
                                {activeTemplate?.isCustom && (
                                    <button
                                        onClick={handleReset}
                                        disabled={resetTemplate.isPending}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-border bg-card hover:bg-accent transition-colors"
                                    >
                                        <RotateCcw size={14} />
                                        Reset
                                    </button>
                                )}
                                <button
                                    onClick={handleSave}
                                    disabled={!dirty || updateTemplate.isPending}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md bg-foreground text-background hover:bg-foreground/90 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                                >
                                    <Save size={14} />
                                    Save
                                </button>
                            </div>
                        </div>

                        {showPreview ? (
                            <div className="p-4">
                                <div className="mb-3">
                                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                        Subject
                                    </span>
                                    <p className="text-sm mt-1">
                                        {subject.replace(/\{\{(\w+)\}\}/g, (_, key) => SAMPLE_VARIABLES[key] ?? key)}
                                    </p>
                                </div>
                                <div className="border border-border rounded-md bg-white overflow-hidden">
                                    <iframe
                                        srcDoc={renderPreview(body)}
                                        className="w-full border-0"
                                        style={{ minHeight: 400 }}
                                        sandbox=""
                                        title="Email preview"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="p-4 space-y-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                        Subject
                                    </label>
                                    <input
                                        type="text"
                                        value={subject}
                                        onChange={(e) => {
                                            setSubject(e.target.value)
                                            setDirty(true)
                                        }}
                                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                        Body (HTML)
                                    </label>
                                    <textarea
                                        value={body}
                                        onChange={(e) => {
                                            setBody(e.target.value)
                                            setDirty(true)
                                        }}
                                        rows={18}
                                        spellCheck={false}
                                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring transition-colors resize-y"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
